/**
 * Button Price Manager - Generic Web Component
 *
 * Manages dynamic pricing display on "Add to Cart" buttons.
 * Listens for price update events and formats button text accordingly.
 *
 * Usage:
 *   <button-price-manager>
 *     <button type="submit">Ajouter au panier</button>
 *   </button-price-manager>
 *
 * Events listened:
 *   - updateButtonPrice: { total: number, currency: string, locale: string }
 *   - resetButtonPrice: resets button to original text
 *   - quantityMeterTierChanged: { discount: number, tierIndex: number, tier: object, quantity: number }
 *
 * Features:
 *   - Decoupled from any specific component (bundle, upsell, etc.)
 *   - Supports multiple currency formats
 *   - Preserves original button text
 *   - Handles button states (loading, disabled, etc.)
 *   - Displays tier-based discount percentage (e.g., "89€ (-10%)")
 */

class ButtonPriceManager extends HTMLElement {
  constructor() {
    super();

    // Find the submit button (can be any button inside)
    this.button = this.querySelector('button[type="submit"]');

    if (!this.button) {
      console.warn('ButtonPriceManager: No submit button found');
      return;
    }

    // Store original button text
    this.originalText = this.getButtonText();

    // Default configuration
    this.config = {
      locale: this.getAttribute('locale') || 'fr-FR',
      currency: this.getAttribute('currency') || 'EUR',
      showCurrencySymbol: this.getAttribute('show-currency-symbol') !== 'false',
      separator: this.getAttribute('separator') || ' - ',
      format: this.getAttribute('format') || '{text}{separator}{price}'
    };

    // Current tier discount (for display)
    this.currentDiscount = 0;
    this.currentTotal = 0;
  }

  connectedCallback() {
    // Listen for price update events
    this.boundUpdatePrice = this.handlePriceUpdate.bind(this);
    this.boundResetPrice = this.handlePriceReset.bind(this);
    this.boundTierChanged = this.handleTierChanged.bind(this);

    document.addEventListener('updateButtonPrice', this.boundUpdatePrice);
    document.addEventListener('resetButtonPrice', this.boundResetPrice);
    document.addEventListener('quantityMeterTierChanged', this.boundTierChanged);
  }

  disconnectedCallback() {
    // Cleanup event listeners
    document.removeEventListener('updateButtonPrice', this.boundUpdatePrice);
    document.removeEventListener('resetButtonPrice', this.boundResetPrice);
    document.removeEventListener('quantityMeterTierChanged', this.boundTierChanged);
  }

  /**
   * Get current button text (from .btn--text or direct textContent)
   */
  getButtonText() {
    const textElement = this.button.querySelector('.btn--text');
    return textElement ? textElement.textContent.trim() : this.button.textContent.trim();
  }

  /**
   * Set button text (update .btn--text or direct textContent)
   */
  setButtonText(text) {
    const textElement = this.button.querySelector('.btn--text');
    if (textElement) {
      textElement.textContent = text;
    } else {
      this.button.textContent = text;
    }
  }

  /**
   * Handle tier changed event (from quantity meter)
   * @param {CustomEvent} event - Event with detail: { discount, tierIndex, tier, quantity }
   */
  handleTierChanged(event) {
    if (!event.detail) return;

    const { discount } = event.detail;
    this.currentDiscount = discount || 0;

    // Re-render button if price is already displayed
    if (this.currentTotal > 0) {
      this.renderButtonPrice();
    }
  }

  /**
   * Handle price update event
   * @param {CustomEvent} event - Event with detail: { total, currency?, locale? }
   */
  handlePriceUpdate(event) {
    if (!event.detail || typeof event.detail.total === 'undefined') {
      console.warn('ButtonPriceManager: Invalid event data', event.detail);
      return;
    }

    const { total, currency, locale } = event.detail;

    // Store current values
    this.currentTotal = total;
    this.currentCurrency = currency || this.config.currency;
    this.currentLocale = locale || this.config.locale;

    // Render button
    this.renderButtonPrice();
  }

  /**
   * Render button price with or without discount
   */
  renderButtonPrice() {
    if (this.currentTotal === 0) return;

    const total = this.currentTotal;
    const currency = this.currentCurrency || this.config.currency;
    const locale = this.currentLocale || this.config.locale;

    let priceDisplay = '';

    if (this.currentDiscount > 0) {
      // Calculer le prix avec remise
      const discountedTotal = total * (1 - this.currentDiscount / 100);

      // Format des deux prix
      const formattedDiscountedPrice = this.formatPrice(discountedTotal, currency, locale);
      const formattedOriginalPrice = this.formatPrice(total, currency, locale);

      // Afficher: prix remisé + ancien prix barré
      priceDisplay = `<span>${formattedDiscountedPrice}</span> <span style="text-decoration: line-through; opacity: 0.6; font-weight:400;">${formattedOriginalPrice}</span>`;
    } else {
      // Pas de remise, afficher le prix normal
      const formattedPrice = this.formatPrice(total, currency, locale);
      priceDisplay = formattedPrice;
    }

    // Update button text
    const newText = this.config.format
      .replace('{text}', this.originalText)
      .replace('{separator}', this.config.separator)
      .replace('{price}', priceDisplay);

    // Use innerHTML instead of textContent to preserve HTML
    const textElement = this.button.querySelector('.btn--text');
    if (textElement) {
      textElement.innerHTML = newText;
    } else {
      this.button.innerHTML = newText;
    }
  }

  /**
   * Handle price reset event
   */
  handlePriceReset() {
    this.setButtonText(this.originalText);
  }

  /**
   * Format price according to locale and currency
   * @param {number} amount - Price amount (in cents or main unit)
   * @param {string} currency - Currency code (EUR, USD, etc.)
   * @param {string} locale - Locale string (fr-FR, en-US, etc.)
   * @returns {string} Formatted price string
   */
  formatPrice(amount, currency, locale) {
    // Convert cents to main unit if needed (assume amount > 100 means it's in cents)
    // This is a heuristic - adjust based on your data structure
    const finalAmount = amount;

    try {
      if (this.config.showCurrencySymbol) {
        // Use Intl.NumberFormat for proper locale formatting
        const formatter = new Intl.NumberFormat(locale, {
          style: 'currency',
          currency: currency,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        });
        return formatter.format(finalAmount);
      } else {
        // Just format the number without currency symbol
        const formatter = new Intl.NumberFormat(locale, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        });
        return formatter.format(finalAmount);
      }
    } catch (error) {
      console.error('ButtonPriceManager: Error formatting price', error);
      // Fallback to simple formatting
      return `${finalAmount.toFixed(2)} ${currency}`;
    }
  }
}

// Register custom element
customElements.define('button-price-manager', ButtonPriceManager);
